import { call, put } from 'redux-saga/effects';

import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { assertUnreachable } from '~commonInfra/utils/TypescriptHelpers';
import { CERTIFIED_INDICATOR_TYPE } from '~src/catalog/components/dataQuality/DataQualityType';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { DataSources } from '~src/datasource/resources/Datasources';
import { PublishedConnections } from '~src/publishedConnection/resources/PublishedConnections';
import { PublishedConnectionTables } from '~src/publishedConnectionTable/resources/PublishedConnectionTables';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { reloadSpaceIfUpdatingCurrentSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';

import { requestFailed } from '../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../codegen/Localize';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { SetCertificationAction } from '../actions/SetCertificationAction';
import { CertificationDialogState, getCertificationActionDialogOptions } from '../components/CertificationDialog';
import { Databases } from '../resources/Databases';
import { Tables } from '../resources/Tables';
import { setOrDeleteLabel } from '../restApi/labels/Labels';
import { isLabelContentType } from '../restApi/labels/LabelTypes';

export function* handleSetCertification(action: SetCertificationAction) {
  try {
    yield call(openCertificationDialog, action);

    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.item]);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

function setCertification(item: SetCertificationAction['item'], args: CertificationDialogState): Promise<IResult> {
  if (!FeatureFlags.columnDQWsEnabled) {
    switch (item.contentType) {
      case 'databaseWithCrass':
      case 'database': return Databases.setDatabaseCertification({
        certificationNote: args.certificationNote,
        isCertified: args.isCertified,
        id: item.vizportalId,
        luid: item.luid,
      });
      case 'tableWithCrass':
      case 'table': return Tables.setTableCertification({
        certificationNote: args.certificationNote,
        isCertified: args.isCertified,
        id: item.vizportalId,
        luid: item.luid,
      });
      case 'datasource': return DataSources.setCertification({
        certificationNote: args.certificationNote,
        isCertified: args.isCertified,
        id: item.vizportalId,
        luid: item.luid,
      });
      case 'virtualConnection': return PublishedConnections.setCertification({
        certificationNote: args.certificationNote,
        isCertified: args.isCertified,
        id: item.vizportalId,
        luid: item.luid,
      });
      case 'virtualConnectionTable': return PublishedConnectionTables.setCertification({
        certificationNote: args.certificationNote,
        isCertified: args.isCertified,
        id: item.vizportalId,
        luid: item.luid,
      });
      default: assertUnreachable(item);
    }
  }

  let { contentType, luid } = item;

  if(contentType === 'tableWithCrass') {
    contentType = 'table';
  }
  if(contentType === 'databaseWithCrass') {
    contentType = 'database';
  }

  if (!isLabelContentType(contentType)) {
    throw new Error(`${contentType} is not a certifiable type`);
  }

  if (!luid) {
    throw new Error('an item cannot be certified without a luid');
  }

  return setOrDeleteLabel({
    category: 'certification',
    contentItems: [{ contentType, contentId: luid, contentName: item.name }],
    labelContent: { message: args.certificationNote, active: args.isCertified, elevated: false, value: 'certified' },
  }).then(async t => {
    ToasterService.instance.pop({
      type: ToastType.Info,
      text: Messages.editCertificationSuccessful(),
    });
    return t;
  }).catch(t => {
    ToasterService.instance.pop({
      type: ToastType.Error,
      text: Messages.editCertificationFailure(),
    });
    return t;
  });
}

function getCertificationOrDefault(item: SetCertificationAction['item']): CertificationDialogState {
  const certification = item.dataQualityIndicators?.filter(i => i.type === CERTIFIED_INDICATOR_TYPE)?.shift() || null;

  if (certification) {
    return {
      isCertified: certification.isActive,
      certificationNote: certification.message,
    };
  }

  return {
    isCertified: item.isCertified || false,
    certificationNote: item.certificationNote || ''
  };
}

export function openCertificationDialog(action: SetCertificationAction) {
  let { contentType, luid: contentId, name: contentName } = action.item;

  if(contentType === 'tableWithCrass') {
    contentType = 'table';
  }
  if(contentType === 'databaseWithCrass') {
    contentType = 'database';
  }

  if (!isLabelContentType(contentType)) {
    throw new Error(`${contentType} is not a certifiable type`);
  }

  const modalInstance = openReactActionDialog(getCertificationActionDialogOptions({
    initialBodyProps: {
      contentItem: {
        contentId,
        contentName,
        contentType,
      }
    },
    initialFormData: getCertificationOrDefault(action.item),
    doAction: (formData) => setCertification(action.item, formData)
  }));

  return modalInstance.result;
}
